import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, onSnapshot, collection, addDoc, query, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { Loader2, Sparkles } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { debounce } from 'lodash';
import ConfirmationSubModal from '../../components/ui/ConfirmationSubModal';

const SubscriptionManagement = () => {
  const functions = getFunctions();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const auth = getAuth();
  const currentUser = auth.currentUser;

  // Update the debug logging effect
  useEffect(() => {
    const debouncedLog = debounce(() => {
      console.log("Current subscription data:", subscriptionData);
      if (subscriptionData) {
        console.log("Subscription details:", {
          status: subscriptionData.status,
          cancel_at_period_end: subscriptionData.cancel_at_period_end,
          id: subscriptionData.id || subscriptionData.subscription
        });
      }
      console.log("Current user data:", userData);
    }, 1000);

    debouncedLog();

    return () => {
      debouncedLog.cancel();
    };
  }, [subscriptionData, userData]);

  // Update the main data fetching effect
  useEffect(() => {
    if (!currentUser) return;

    let isMounted = true;

    const userUnsubscribe = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (snapshot) => {
        if (isMounted && snapshot.exists()) {
          setUserData(snapshot.data());
        }
        if (isMounted) setLoading(false);
      },
      (error) => {
        console.error("Error fetching user data:", error);
        if (isMounted) {
          setError(error.message);
          setLoading(false);
        }
      }
    );

    const subscriptionsRef = collection(db, 'customers', currentUser.uid, 'subscriptions');
    const subscriptionQuery = query(subscriptionsRef, limit(1));
    
    const subscriptionUnsubscribe = onSnapshot(
      subscriptionQuery,
      (snapshot) => {
        if (isMounted && !snapshot.empty) {
          setSubscriptionData(snapshot.docs[0].data());
        }
      },
      (error) => {
        if (isMounted) {
          console.error("Error fetching subscription data:", error);
        }
      }
    );

    return () => {
      isMounted = false;
      userUnsubscribe();
      subscriptionUnsubscribe();
    };
  }, [currentUser]);

  const handleSubscriptionAction = async () => {
    if (!currentUser) return;

    // If user is already subscribed, show confirmation modal
    if (userData?.tier === 'pro') {
      setIsConfirmModalOpen(true);
      return;
    }

    // Otherwise proceed with subscription flow
    setLoading(true);
    setError(null);

    try {
      const customerRef = doc(db, 'customers', currentUser.uid);
      const sessionsRef = collection(customerRef, 'checkout_sessions');
      
      const sessionConfig = {
        mode: 'subscription',
        allow_promotion_codes: true,
        success_url: window.location.origin + '/chat',
        cancel_url: window.location.origin + '/settings',
        line_items: [{
          price: 'price_1SRGM0HHaluHTLHlZVE3BtTU', // $399.99 price
          // price: 'price_1QbB4pHHaluHTLHlU3zl3R4i', // Old $9.99 price
          quantity: 1
        }],
        customer_email: currentUser.email,
        metadata: {
          userId: currentUser.uid,
          userEmail: currentUser.email
        }
      };

      const sessionDoc = await addDoc(sessionsRef, sessionConfig);

      const unsubscribe = onSnapshot(sessionDoc, (snap) => {
        const data = snap.data();
        if (data?.error) {
          setError(data.error.message);
          setLoading(false);
        }
        if (data?.url) {
          window.location.assign(data.url);
        }
      });

      setTimeout(() => {
        unsubscribe();
        if (loading) {
          setLoading(false);
          setError("Timeout while waiting for portal URL");
        }
      }, 10000);

    } catch (error) {
      console.error("Error managing subscription:", error);
      setError(error.message);
      setLoading(false);
    }
  };

  // Modified handleCancelConfirm function to fix the invalid mode error
const handleCancelConfirm = async () => {
  setLoading(true);
  try {
    const customerRef = doc(db, 'customers', currentUser.uid);
    // Change from checkout_sessions to billing_portal_sessions
    const sessionsRef = collection(customerRef, 'billing_portal_sessions');
    
    const sessionConfig = {
      return_url: window.location.origin + '/settings',
      // customer_email is not needed for billing portal, but keeping it won't hurt
      customer_email: currentUser.email
      // Remove the invalid mode parameter
    };

    const sessionDoc = await addDoc(sessionsRef, sessionConfig);

    const unsubscribe = onSnapshot(sessionDoc, (snap) => {
      const data = snap.data();
      if (data?.error) {
        setError(data.error.message);
        setLoading(false);
      }
      if (data?.url) {
        window.location.assign(data.url);
      }
    });

    setTimeout(() => {
      unsubscribe();
      if (loading) {
        setLoading(false);
        setError("Timeout while waiting for portal URL");
      }
    }, 10000);
  } catch (error) {
    console.error("Error accessing billing portal:", error);
    setError(error.message);
    setLoading(false);
  } finally {
    setIsConfirmModalOpen(false);
  }
};

  // const handleCancelConfirm = async () => {
  //   setLoading(true);
  //   try {
  //     const customerRef = doc(db, 'customers', currentUser.uid);
  //     const sessionsRef = collection(customerRef, 'checkout_sessions');
      
  //     const sessionConfig = {
  //       return_url: window.location.origin + '/settings',
  //       customer_email: currentUser.email,
  //       mode: 'billing_portal'
  //     };

  //     const sessionDoc = await addDoc(sessionsRef, sessionConfig);

  //     const unsubscribe = onSnapshot(sessionDoc, (snap) => {
  //       const data = snap.data();
  //       if (data?.error) {
  //         setError(data.error.message);
  //         setLoading(false);
  //       }
  //       if (data?.url) {
  //         window.location.assign(data.url);
  //       }
  //     });

  //     setTimeout(() => {
  //       unsubscribe();
  //       if (loading) {
  //         setLoading(false);
  //         setError("Timeout while waiting for portal URL");
  //       }
  //     }, 10000);
  //   } catch (error) {
  //     console.error("Error accessing billing portal:", error);
  //     setError(error.message);
  //     setLoading(false);
  //   } finally {
  //     setIsConfirmModalOpen(false);
  //   }
  // };

  const getSubscriptionStatus = () => {
    if (!subscriptionData) return 'Unknown';
    
    if (subscriptionData.cancel_at_period_end) {
      return 'Canceling at period end';
    }
    if (subscriptionData.canceled_at) {
      return 'Canceled';
    }
    if (subscriptionData.status === 'active') {
      return 'Active';
    }
    return subscriptionData.status;
  };

  const getCurrentPeriodEnd = () => {
    if (!subscriptionData?.current_period_end) return null;
    return new Date(subscriptionData.current_period_end.seconds * 1000).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="animate-spin text-emerald-500" size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/20 text-red-200 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  // User not subscribed
  if (!userData || userData.tier !== 'pro') {
    return (
      <div className="bg-emerald-900/30 rounded-lg p-6">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Sparkles className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl text-white">Upgrade to Pro</h2>
        </div>

        <ul className="space-y-2 mb-6 text-gray-300">
          <li className="flex items-center gap-2">
            <span className="text-emerald-400">✓</span>
            Extended Access! (14x more chatting power)
          </li>
          <li className="flex items-center gap-2">
            <span className="text-emerald-400">✓</span>
            Advanced betting insights
          </li>
          <li className="flex items-center gap-2">
            <span className="text-emerald-400">✓</span>
            Up to 50% discount on StarCoins
          </li>
          <li className="flex items-center gap-2">
            <span className="text-emerald-400">✓</span>
            Priority support
          </li>
        </ul>

        <button
          onClick={handleSubscriptionAction}
          disabled={loading}
          className={`
            w-full bg-emerald-500 hover:bg-emerald-600 text-white
            font-medium py-2 px-4 rounded-lg transition-colors
            ${loading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {loading ? 'Loading...' : 'Subscribe Now - $399.99/month'}
        </button>
      </div>
    );
  }

  // User is subscribed
  return (
    <div className="bg-emerald-900/30 rounded-lg p-6">
      <h2 className="text-xl text-white mb-4">Subscription Status</h2>
      
      <div className="space-y-4 mb-6">
        <div className="flex justify-between text-gray-300">
          <span>Current Plan:</span>
          <span className="font-medium capitalize">{userData?.tier || 'Basic'}</span>
        </div>
        <div className="flex justify-between text-gray-300">
          <span>Status:</span>
          <span className="font-medium text-emerald-400">
            {getSubscriptionStatus()}
          </span>
        </div>
        {getCurrentPeriodEnd() && (
          <div className="flex justify-between text-gray-300">
            <span>Current Period Ends:</span>
            <span className="font-medium">{getCurrentPeriodEnd()}</span>
          </div>
        )}
      </div>

      <button
        onClick={handleSubscriptionAction}
        disabled={loading}
        className={`
          w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 
          font-medium py-2 px-4 rounded-lg transition-colors
          ${loading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        {loading ? 'Loading...' : 'Manage Subscription'}
      </button>
      
      <p className="text-sm text-gray-400 mt-4 text-center">
        Manage or cancel your subscription through Stripe's secure portal
      </p>

      <ConfirmationSubModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleCancelConfirm}
        message="Are you sure you want to manage your subscription? You'll be redirected to the Stripe portal where you can make changes or cancel your subscription."
      />
    </div>
  );
};

export default SubscriptionManagement;


// Current best working version
// import React, { useState, useEffect } from 'react';
// import { getAuth } from 'firebase/auth';
// import { doc, onSnapshot, collection, addDoc, query, limit } from 'firebase/firestore';
// import { db } from '../../firebase';
// import { Loader2, Sparkles } from 'lucide-react';
// import { getFunctions, httpsCallable } from 'firebase/functions';
// import { debounce } from 'lodash';
// import ConfirmationSubModal from '../../components/ui/ConfirmationSubModal';


// const SubscriptionManagement = () => {
//   const functions = getFunctions();
//   const [loading, setLoading] = useState(true);
//   const [userData, setUserData] = useState(null);
//   const [subscriptionData, setSubscriptionData] = useState(null);
//   const [error, setError] = useState(null);
//   const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

//   const auth = getAuth();
//   const currentUser = auth.currentUser;

// // Update the debug logging effect
// useEffect(() => {
//   const debouncedLog = debounce(() => {
//     console.log("Current subscription data:", subscriptionData);
//     if (subscriptionData) {
//       console.log("Subscription details:", {
//         status: subscriptionData.status,
//         cancel_at_period_end: subscriptionData.cancel_at_period_end,
//         id: subscriptionData.id || subscriptionData.subscription
//       });
//     }
//     console.log("Current user data:", userData);
//   }, 1000); // Wait 1 second between logs

//   debouncedLog();

//   return () => {
//     debouncedLog.cancel();
//   };
// }, [subscriptionData, userData]);

// // Update the main data fetching effect
// useEffect(() => {
//   if (!currentUser) return;

//   let isMounted = true;

// // Listen to user document changes
// const userUnsubscribe = onSnapshot(
//   doc(db, 'users', currentUser.uid),
//   (snapshot) => {
//     if (isMounted && snapshot.exists()) {
//       setUserData(snapshot.data());
//     }
//     if (isMounted) setLoading(false);
//   },
//   (error) => {
//     console.error("Error fetching user data:", error);
//     if (isMounted) {
//       setError(error.message);
//       setLoading(false);
//     }
//   }
// );

//     // Listen to subscription changes
//   const subscriptionsRef = collection(db, 'customers', currentUser.uid, 'subscriptions');
//   const subscriptionQuery = query(subscriptionsRef, limit(1));
  
//   const subscriptionUnsubscribe = onSnapshot(
//     subscriptionQuery,
//     (snapshot) => {
//       if (isMounted && !snapshot.empty) {
//         setSubscriptionData(snapshot.docs[0].data());
//       }
//     },
//     (error) => {
//       if (isMounted) {
//         console.error("Error fetching subscription data:", error);
//       }
//     }
//   );

//   return () => {
//     isMounted = false;
//     userUnsubscribe();
//     subscriptionUnsubscribe();
//   };
// }, [currentUser]);

//   const handleSubscriptionAction = async () => {
//     if (!currentUser) return;

//     setLoading(true);
//     setError(null);

//     try {
//       const customerRef = doc(db, 'customers', currentUser.uid);
//       const sessionsRef = collection(customerRef, 'checkout_sessions');
      
//       // Different session configuration based on subscription status
//       const sessionConfig = userData?.tier === 'pro' 
//         ? {
//             return_url: window.location.origin + '/settings',
//             customer_email: currentUser.email,
//             mode: 'billing_portal'
//           }
//         : {
//             mode: 'subscription',
//             allow_promotion_codes: true,
//             success_url: window.location.origin + '/dashboard',
//             cancel_url: window.location.origin + '/settings',
//             line_items: [{
//               price: 'price_1QbB4pHHaluHTLHlU3zl3R4i',
//               quantity: 1
//             }],
//             customer_email: currentUser.email,
//             metadata: {
//               userId: currentUser.uid,
//               userEmail: currentUser.email
//             }
//           };

//       const sessionDoc = await addDoc(sessionsRef, sessionConfig);

//       const unsubscribe = onSnapshot(sessionDoc, (snap) => {
//         const data = snap.data();
//         if (data?.error) {
//           setError(data.error.message);
//           setLoading(false);
//         }
//         if (data?.url) {
//           window.location.assign(data.url);
//         }
//       });

//       setTimeout(() => {
//         unsubscribe();
//         if (loading) {
//           setLoading(false);
//           setError("Timeout while waiting for portal URL");
//         }
//       }, 10000);

//     } catch (error) {
//       console.error("Error managing subscription:", error);
//       setError(error.message);
//       setLoading(false);
//     }
//   };

//   const getSubscriptionStatus = () => {
//     if (!subscriptionData) return 'Unknown';
    
//     if (subscriptionData.cancel_at_period_end) {
//       return 'Canceling at period end';
//     }
//     if (subscriptionData.canceled_at) {
//       return 'Canceled';
//     }
//     if (subscriptionData.status === 'active') {
//       return 'Active';
//     }
//     return subscriptionData.status;
//   };

//   const getCurrentPeriodEnd = () => {
//     if (!subscriptionData?.current_period_end) return null;
//     return new Date(subscriptionData.current_period_end.seconds * 1000).toLocaleDateString();
//   };

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center p-8">
//         <Loader2 className="animate-spin text-emerald-500" size={24} />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="bg-red-500/20 text-red-200 p-4 rounded-lg">
//         {error}
//       </div>
//     );
//   }

//   // User not subscribed
//   if (!userData || userData.tier !== 'pro') {
//     return (
//       <div className="bg-emerald-900/30 rounded-lg p-6">
//         <div className="flex items-center justify-center gap-2 mb-4">
//           <Sparkles className="w-6 h-6 text-emerald-400" />
//           <h2 className="text-xl text-white">Upgrade to Pro</h2>
//         </div>

//         <ul className="space-y-2 mb-6 text-gray-300">
//           <li className="flex items-center gap-2">
//             <span className="text-emerald-400">✓</span>
//             Extended Access!
//           </li>
//           <li className="flex items-center gap-2">
//             <span className="text-emerald-400">✓</span>
//             Advanced betting insights
//           </li>
//           <li className="flex items-center gap-2">
//             <span className="text-emerald-400">✓</span>
//             Priority support
//           </li>
//         </ul>

//         <button
//           onClick={handleSubscriptionAction}
//           disabled={loading}
//           className={`
//             w-full bg-emerald-500 hover:bg-emerald-600 text-white
//             font-medium py-2 px-4 rounded-lg transition-colors
//             ${loading ? 'opacity-50 cursor-not-allowed' : ''}
//           `}
//         >
//           {loading ? 'Loading...' : 'Subscribe Now - $9.99/month'}
//         </button>
//       </div>
//     );
//   }

//   // User is subscribed
//   return (
//     <div className="bg-emerald-900/30 rounded-lg p-6">
//       <h2 className="text-xl text-white mb-4">Subscription Status</h2>
      
//       <div className="space-y-4 mb-6">
//         <div className="flex justify-between text-gray-300">
//           <span>Current Plan:</span>
//           <span className="font-medium capitalize">{userData?.tier || 'Basic'}</span>
//         </div>
//         <div className="flex justify-between text-gray-300">
//           <span>Status:</span>
//           <span className="font-medium text-emerald-400">
//             {getSubscriptionStatus()}
//           </span>
//         </div>
//         {getCurrentPeriodEnd() && (
//           <div className="flex justify-between text-gray-300">
//             <span>Current Period Ends:</span>
//             <span className="font-medium">{getCurrentPeriodEnd()}</span>
//           </div>
//         )}
//       </div>

//       <button
//         onClick={handleSubscriptionAction}
//         disabled={loading}
//         className={`
//           w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 
//           font-medium py-2 px-4 rounded-lg transition-colors
//           ${loading ? 'opacity-50 cursor-not-allowed' : ''}
//         `}
//       >
//         {loading ? 'Loading...' : 'Manage Subscription'}
//       </button>

//       {/* {userData?.tier === 'pro' && (
//         <button
//         onClick={async () => {
//           try {
//             setLoading(true);
//             const actualSubscriptionId = subscriptionData?.stripeLink?.split('/').pop();
//             console.log("Found subscription ID:", actualSubscriptionId);
            
//             if (!actualSubscriptionId) {
//               throw new Error('No valid subscription ID found');
//             }
        
//             const syncSubscription = httpsCallable(functions, 'syncSubscriptionStatus');
//             const result = await syncSubscription({
//               userId: currentUser.uid,
//               subscriptionId: actualSubscriptionId
//             });
//             console.log('Sync result:', result.data);
//           } catch (error) {
//             console.error('Sync error:', error);
//             setError(error.message);
//           } finally {
//             setLoading(false);
//           }
//         }}
//           className="w-full mt-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 
//             font-medium py-2 px-4 rounded-lg transition-colors"
//         >
//           Force Sync with Stripe
//         </button>
//       )} */}
      
//       <p className="text-sm text-gray-400 mt-4 text-center">
//         Manage or cancel your subscription through Stripe's secure portal
//       </p>
//     </div>
//   );
// };

// export default SubscriptionManagement;





// import React, { useState, useEffect } from 'react';
// import { getAuth } from 'firebase/auth';
// import { doc, onSnapshot, collection, addDoc } from 'firebase/firestore';
// import { db } from '../../firebase'; 
// import { Loader2, Sparkles } from 'lucide-react';

// const SubscriptionManagement = () => {
//   const [loading, setLoading] = useState(true);
//   const [userData, setUserData] = useState(null);
//   const [error, setError] = useState(null);
  
//   const auth = getAuth();
//   const currentUser = auth.currentUser;

//   useEffect(() => {
//     console.log('DB instance:', db);
//     console.log('Current user:', currentUser);
//     if (!currentUser) return;

//     const unsubscribe = onSnapshot(
//       doc(db, 'users', currentUser.uid),
//       (snapshot) => {
//         if (snapshot.exists()) {
//           setUserData(snapshot.data());
//         }
//         setLoading(false);
//       },
//       (error) => {
//         console.error("Error fetching user data:", error);
//         setError(error.message);
//         setLoading(false);
//       }
//     );

//     return () => unsubscribe();
//   }, [currentUser]);

//   const createPortalSession = async () => {
//     try {
//       console.log('Starting portal session creation...', currentUser.uid);
      
//       const customerRef = doc(db, 'customers', currentUser.uid);
//       console.log('Customer ref created:', customerRef.path);
      
//       const portalSessionsRef = collection(customerRef, 'billing_portal_sessions');
//       console.log('Portal sessions ref created:', portalSessionsRef.path);
      
//       console.log('Adding portal session document...');
//       const portalSession = await addDoc(portalSessionsRef, {
//         return_url: window.location.origin + '/settings',
//       });
//       console.log('Portal session created:', portalSession.id);
  
//       const unsubscribe = onSnapshot(portalSession, (snap) => {
//         console.log('Portal session snapshot:', snap.data());
//         const data = snap.data();
//         if (data?.url) {
//           console.log('Redirecting to:', data.url);
//           window.location.assign(data.url);
//         }
//         if (data?.error) {
//           console.error('Portal session error:', data.error);
//           setError(data.error.message);
//           setLoading(false);
//         }
//       });
  
//       setTimeout(() => {
//         unsubscribe();
//         if (loading) {
//           console.log('Portal session timeout');
//           setLoading(false);
//           setError("Timeout while waiting for portal URL");
//         }
//       }, 10000);
//     } catch (error) {
//       console.error('Portal session creation error:', error);
//       throw error;  // Re-throw to be handled by the caller
//     }
//   };

//   const handleSubscriptionAction = async () => {
//     if (!currentUser) {
//       console.log('No current user found');
//       return;
//     }

//     setLoading(true);
//     setError(null);

//     try {
//       const customerRef = doc(db, 'customers', currentUser.uid);
      
//       if (userData?.tier === 'pro') {
//         await createPortalSession();
//       } else {
//         const checkoutSessionsRef = collection(customerRef, 'checkout_sessions');
//         const sessionDoc = await addDoc(checkoutSessionsRef, {
//           mode: 'subscription',
//           allow_promotion_codes: true,
//           success_url: window.location.origin + '/dashboard',
//           cancel_url: window.location.origin + '/settings',
//           line_items: [{
//             price: 'price_1QbB4pHHaluHTLHlU3zl3R4i',
//             quantity: 1
//           }],
//           customer_email: currentUser.email,
//           metadata: {
//             userId: currentUser.uid,
//             userEmail: currentUser.email
//           }
//         });

//         const unsubscribe = onSnapshot(sessionDoc, (snap) => {
//           const data = snap.data();
//           if (data?.url) {
//             window.location.assign(data.url);
//           }
//           if (data?.error) {
//             setError(data.error.message);
//             setLoading(false);
//           }
//         });

//         setTimeout(() => {
//           unsubscribe();
//           if (loading) {
//             setLoading(false);
//             setError("Timeout while waiting for checkout URL");
//           }
//         }, 10000);
//       }
//     } catch (error) {
//       console.error("Error managing subscription:", error);
//       setError(error.message);
//       setLoading(false);
//     }
//   };

//   const handleCancelSubscription = async () => {
//     if (!currentUser) {
//       console.log('No current user');
//       return;
//     }
    
//     if (!window.confirm("Are you sure you want to manage your subscription?")) {
//       return;
//     }
  
//     console.log('Starting subscription management for user:', currentUser.uid);
//     setLoading(true);
//     setError(null);
  
//     try {
//       await createPortalSession();
//     } catch (error) {
//       console.error('Error in handleCancelSubscription:', error);
//       setError(error.message || 'Failed to access billing portal');
//       setLoading(false);
//     }
//   };
  
//   if (loading) {
//     return (
//       <div className="flex items-center justify-center p-8">
//         <Loader2 className="animate-spin text-emerald-500" size={24} />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="bg-red-500/20 text-red-200 p-4 rounded-lg">
//         {error}
//       </div>
//     );
//   }

//   // User not subscribed
//   if (!userData || userData.tier !== 'pro') {
//     return (
//       <div className="bg-emerald-900/30 rounded-lg p-6">
//         <div className="flex items-center justify-center gap-2 mb-4">
//           <Sparkles className="w-6 h-6 text-emerald-400" />
//           <h2 className="text-xl text-white">Upgrade to Pro</h2>
//         </div>

//         <ul className="space-y-2 mb-6 text-gray-300">
//           <li className="flex items-center gap-2">
//             <span className="text-emerald-400">✓</span>
//             Unlimited messages
//           </li>
//           <li className="flex items-center gap-2">
//             <span className="text-emerald-400">✓</span>
//             Advanced betting insights
//           </li>
//           <li className="flex items-center gap-2">
//             <span className="text-emerald-400">✓</span>
//             Priority support
//           </li>
//         </ul>

//         <button
//           onClick={handleSubscriptionAction}
//           disabled={loading}
//           className={`
//             w-full bg-emerald-500 hover:bg-emerald-600 text-white
//             font-medium py-2 px-4 rounded-lg transition-colors
//             ${loading ? 'opacity-50 cursor-not-allowed' : ''}
//           `}
//         >
//           {loading ? 'Loading...' : 'Subscribe Now - $9.99/month'}
//         </button>
//       </div>
//     );
//   }

//   // User is subscribed (pro)
//   return (
//     <div className="bg-emerald-900/30 rounded-lg p-6">
//       <h2 className="text-xl text-white mb-4">Subscription Status</h2>
      
//       <div className="space-y-4 mb-6">
//         <div className="flex justify-between text-gray-300">
//           <span>Current Plan:</span>
//           <span className="font-medium capitalize">{userData.tier}</span>
//         </div>
//         <div className="flex justify-between text-gray-300">
//           <span>Status:</span>
//           <span className="font-medium text-emerald-400">
//             {userData.subscriptionStatus || 'Active'}
//           </span>
//         </div>
//       </div>

//       <div className="flex flex-col gap-2">
//         <button
//           onClick={handleSubscriptionAction}
//           disabled={loading}
//           className={`
//             w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 
//             font-medium py-2 px-4 rounded-lg transition-colors
//             ${loading ? 'opacity-50 cursor-not-allowed' : ''}
//           `}
//         >
//           {loading ? 'Loading...' : 'Manage Subscription'}
//         </button>
//         <button
//           onClick={handleCancelSubscription}
//           disabled={loading}
//           className={`
//             w-full bg-red-500 hover:bg-red-600 text-white
//             font-medium py-2 px-4 rounded-lg transition-colors
//             ${loading ? 'opacity-50 cursor-not-allowed' : ''}
//           `}
//         >
//           {loading ? 'Loading...' : 'Cancel Subscription'}
//         </button>
//       </div>
      
//       <p className="text-sm text-gray-400 mt-4 text-center">
//         Manage or cancel your subscription through Stripe's secure portal.
//       </p>
//     </div>
//   );
// };

// export default SubscriptionManagement;
